import { Card, Typography } from 'antd'
import styled, { css } from 'styled-components'

const { Text: AntText } = Typography

// ── Design tokens ─────────────────────────────────────────────────────────────
export const TOKEN = {
  blue: '#2563EB',
  purple: '#7C3AED',
  teal: '#0D9488',
  amber: '#D97706',
  red: '#DC2626',
  green: '#16A34A',

  text: '#18181B',
  sub: '#52525B',
  muted: '#A1A1AA',
  border: '#D4D4D8',
  divider: '#E4E4E7',

  bg: '#F4F4F5',
  surface: '#FFFFFF',
  hover: '#F0F5FF',

  blueTint: '#EFF6FF',
  purpleTint: '#F5F3FF',
  tealTint: '#F0FDFA',
}

export const media = {
  lg: '@media (max-width: 992px)',
  md: '@media (max-width: 768px)',
  sm: '@media (max-width: 576px)',
}

const aspectHeaderBg = level => {
  if (level === 0) return 'linear-gradient(90deg, #EFF6FF 0%, #F9FAFB 100%)'
  if (level === 1) return 'linear-gradient(90deg, #F5F3FF 0%, #F9FAFB 100%)'
  return '#FAFAFA'
}

// ── Page layout ───────────────────────────────────────────────────────────────
export const PageActions = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;

  ${media.sm} {
    width: 100%;

    .ant-btn {
      flex: 1 1 auto;
      min-width: 0;
    }
  }
`

export const KpiContentBlock = styled.div`
  min-height: calc(100vh - 150px);
  background: ${TOKEN.bg};
  border-radius: 12px;

  ${media.md} {
    min-height: calc(100vh - 130px);
    border-radius: 10px;
  }

  ${media.sm} {
    min-height: calc(100vh - 110px);
    border-radius: 8px;
  }
`

export const PageInner = styled.div`
  display: flex;
  flex-direction: column;
`

export const TabBar = styled.div`
  display: flex;
  gap: 0;
  border-bottom: 2px solid ${TOKEN.divider};
  margin-bottom: 16px;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }

  ${media.sm} {
    margin-bottom: 12px;
  }
`

export const TabItem = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 9px 18px;
  cursor: pointer;
  border-bottom: 3px solid
    ${props => (props.$active ? props.$color : 'transparent')};
  margin-bottom: -2px;
  background: ${props =>
    props.$active ? `${props.$color}0D` : 'transparent'};
  border-radius: 8px 8px 0 0;
  transition: all 0.18s;
  user-select: none;
  flex-shrink: 0;
  white-space: nowrap;

  ${media.sm} {
    padding: 8px 14px;
  }
`

export const TabLabel = styled.div`
  font-weight: ${props => (props.$active ? 700 : 500)};
  font-size: 13px;
  color: ${props => (props.$active ? props.$color : TOKEN.sub)};
  line-height: 1.3;

  ${media.sm} {
    font-size: 12px;
  }
`

export const FilterCard = styled(Card)`
  margin-bottom: 14px;
  border-radius: 10px;
  border: 1px solid ${TOKEN.border};

  .ant-card-body {
    padding: 10px 16px;
  }

  ${media.sm} {
    margin-bottom: 10px;
    border-radius: 8px;

    .ant-card-body {
      padding: 10px 12px;
    }
  }
`

export const FilterRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;

  ${media.sm} {
    gap: 8px;
  }
`

export const FilterLabel = styled.span`
  font-weight: 500;
  font-size: 13px;
  white-space: nowrap;
  color: ${TOKEN.sub};
`

export const FilterDatePickerWrap = styled.div`
  flex: 1;
  min-width: 200px;
  max-width: 280px;

  .ant-picker {
    width: 100%;
    border-radius: 8px;
  }

  ${media.sm} {
    max-width: 100%;
    width: 100%;
    min-width: 0;
  }
`

export const WeightBarText = styled.span`
  font-size: 11px;
  font-weight: 700;
  color: ${props => props.$color || TOKEN.blue};
  white-space: nowrap;
`

// ── Tree ──────────────────────────────────────────────────────────────────────
export const TreeContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-width: 100%;
  overflow: hidden;

  ${media.sm} {
    gap: 8px;
  }
`

// ── Aspect card ───────────────────────────────────────────────────────────────
export const AspectCard = styled.div`
  background: ${TOKEN.surface};
  border-radius: 10px;
  border: 1px solid ${TOKEN.border};
  overflow: hidden;
  transition: box-shadow 0.2s;
  max-width: 100%;
  box-sizing: border-box;

  ${props =>
    props.$level != null &&
    css`
      margin-left: ${props.$level * 16}px;
      margin-bottom: ${props.$level === 0 ? 10 : 6}px;

      ${media.md} {
        margin-left: ${props.$level * 10}px;
      }

      ${media.sm} {
        margin-left: ${props.$level * 6}px;
        margin-bottom: ${props.$level === 0 ? 8 : 5}px;
        border-radius: 8px;
      }
    `}

  &:hover {
    box-shadow: 0 3px 12px rgba(0, 0, 0, 0.08);
  }
`

export const AspectHeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${props => (props.$level === 0 ? '10px 16px' : '8px 14px')};
  background: ${props => aspectHeaderBg(props.$level || 0)};
  border-bottom: 1px solid ${TOKEN.divider};
  min-height: ${props => (props.$level === 0 ? 44 : 38)}px;
  cursor: pointer;
  user-select: none;
  gap: 10px;
  overflow: hidden;

  ${media.sm} {
    padding: 8px 12px;
    min-height: unset;
    gap: 8px;
  }
`

export const AspectHeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1 1 0;
  min-width: 0;
  overflow: hidden;
`

export const AspectHeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex: 0 0 auto;
  flex-shrink: 0;
  margin-left: auto;
`

export const AspectTitle = styled(AntText).attrs({ strong: true })`
  && {
    flex: 1 1 0;
    min-width: 0;
    overflow: hidden;
    font-size: ${props => (props.$level === 0 ? 13 : 12)}px;
    font-weight: 600;
    color: ${TOKEN.text};
  }
`

export const AspectWeightTag = styled.span`
  margin: 0;
  padding: 0 8px;
  font-size: 11px;
  font-weight: 700;
  background: ${props => `${props.$color}14`};
  border: 1px solid ${props => `${props.$color}35`};
  color: ${props => props.$color};
  border-radius: 10px;
  line-height: 20px;
  flex-shrink: 0;
`

export const MoreMenuTrigger = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 6px;
  border: 1px solid ${TOKEN.border};
  cursor: pointer;
  color: ${TOKEN.sub};
  background: #fff;
  flex-shrink: 0;
`

export const AspectExpandedContent = styled.div`
  padding: ${props => (props.$hasContent ? '10px 14px' : '0')};

  ${media.sm} {
    padding: ${props => (props.$hasContent ? '8px 10px' : '0')};
  }
`

export const AspectChildrenList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: ${props => (props.$hasNodes ? 10 : 0)}px;
`

export const AspectHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 16px;
  background: ${TOKEN.blueTint};
  border-bottom: 1px solid ${TOKEN.divider};
  min-height: 44px;
  cursor: pointer;
  user-select: none;
  gap: 10px;
`

export const ChildAspectHeader = styled(AspectHeader)`
  background: ${TOKEN.purpleTint};
  min-height: 38px;
  padding: 8px 14px;
`

export const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
`

export const ActionsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
`

export const ChildAspectContainer = styled.div`
  padding: 8px 14px;
  background: ${TOKEN.purpleTint};
  border-bottom: 1px solid ${TOKEN.divider};
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-left: 20px;
  border-left: 2px solid ${TOKEN.border};

  ${media.sm} {
    margin-left: 10px;
    padding: 8px 10px;
  }
`

// ── KPI tree nodes ─────────────────────────────────────────────────────────────
export const TreeNodeList = styled.div`
  padding: 8px 12px;
  display: flex;
  flex-direction: column;
  gap: 6px;

  ${media.sm} {
    padding: 6px 8px;
    gap: 5px;
  }
`

export const KpiNodeContainer = styled.div`
  position: relative;
  margin-left: ${props => (props.level > 1 ? 24 : 0)}px;

  ${media.md} {
    margin-left: ${props => (props.level > 1 ? 16 : 0)}px;
  }

  ${media.sm} {
    margin-left: ${props => (props.level > 1 ? 10 : 0)}px;
  }

  &::before {
    content: '';
    position: absolute;
    top: -6px;
    bottom: 0;
    left: -14px;
    width: 1px;
    background: ${TOKEN.border};
    display: ${props => (props.level > 1 ? 'block' : 'none')};

    ${media.sm} {
      left: -10px;
    }
  }

  &::after {
    content: '';
    position: absolute;
    top: 20px;
    left: -14px;
    width: 10px;
    height: 1px;
    background: ${TOKEN.border};
    display: ${props => (props.level > 1 ? 'block' : 'none')};

    ${media.sm} {
      left: -10px;
      width: 8px;
    }
  }

  &:last-child::before {
    bottom: calc(100% - 20px);
  }
`

export const KpiChildrenList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 6px;
`

export const KpiRowCard = styled.div`
  display: flex;
  align-items: center;
  padding: 9px 14px;
  background: ${props => (props.active ? TOKEN.hover : TOKEN.surface)};
  border: 1px solid ${props => (props.active ? TOKEN.blue : TOKEN.divider)};
  border-radius: 8px;
  transition: all 0.15s;
  cursor: pointer;
  position: relative;
  z-index: 1;

  ${media.sm} {
    padding: 8px 10px;
    align-items: flex-start;
  }

  &:hover {
    background: ${TOKEN.hover};
    border-color: ${props => (props.active ? TOKEN.blue : TOKEN.border)};
    box-shadow: 0 1px 6px rgba(0, 0, 0, 0.06);
  }
`

export const KpiRowContent = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  gap: 6px;
`

export const KpiRowTop = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  width: 100%;
`

export const ExpandIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  color: ${TOKEN.text};
  transition: transform 0.2s, color 0.15s;
  transform: ${props => (props.expanded ? 'rotate(90deg)' : 'rotate(0deg)')};
  flex-shrink: 0;
  visibility: ${props => (props.$hidden ? 'hidden' : 'visible')};

  & .anticon {
    font-weight: 500;
  }

  &:hover {
    color: ${TOKEN.blue};
  }
`

export const NodeTypeIcon = styled.span`
  display: inline-flex;
  flex-shrink: 0;
  color: ${props => props.$color};
  font-size: ${props => props.$size || 14}px;
`

export const NodeTitle = styled.div`
  display: flex;
  align-items: center;
  flex: 1 1 0;
  min-width: 0;
  overflow: hidden;
  gap: 4px;
`

export const NodeTitleText = styled(AntText)`
  && {
    flex: 1 1 0;
    min-width: 0;
    font-size: 13px;
    font-weight: 500;
    color: ${TOKEN.text};
  }
`

export const KgiBadge = styled.span`
  margin-left: 8px;
  padding: 1px 7px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  background: ${TOKEN.purple}14;
  color: ${TOKEN.purple};
  border: 1px solid ${TOKEN.purple}30;
  flex-shrink: 0;
  cursor: default;

  ${media.sm} {
    margin-left: 0;
    margin-top: 4px;
  }
`

export const NodeMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 14px;
  width: 100%;
  padding-left: 46px;
  box-sizing: border-box;

  ${media.sm} {
    padding-left: 28px;
    gap: 6px 10px;
  }
`

export const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: ${TOKEN.sub};
  flex-shrink: 0;
  max-width: 100%;
  overflow: hidden;
`

export const HandlerWrap = styled.span`
  display: inline-flex;
  cursor: default;
`

export const ExpandedDetailContainer = styled.div`
  margin-top: 6px;
  margin-bottom: 10px;
  margin-left: 24px;
  position: relative;
  z-index: 1;
  animation: kpiFadeIn 0.25s ease;

  ${media.md} {
    margin-left: 16px;
  }

  ${media.sm} {
    margin-left: 0;
    margin-bottom: 8px;
  }

  @keyframes kpiFadeIn {
    from {
      opacity: 0;
      transform: translateY(-4px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`
